/**
 * AUTHORIZATION CODE + PKCE, AND THE OFFLINE RULE THAT SHAPES ALL OF IT.
 * =====================================================================
 *
 * This is the real {@link TokenProvider}. It replaces `devStubTokenProvider` when — and only when — an
 * app client id is configured; see `install.ts` for what happens when there is not one.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * **THE RULE: AN AUTH PROBLEM MUST NEVER COST A TECHNICIAN THEIR WORK.**
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * The platform fixes the lifetimes and this app does not get a vote: **access token 15 minutes, refresh
 * token 10 hours** (the Fresnel/PRISM Authentication HLD). Two consequences run through everything
 * below. First, **renewing before expiry is the ordinary path, not an edge case** — a 15-minute token is
 * renewed dozens of times in a shift, so the renewal has to be quiet, single-flight and early rather
 * than reactive. Second, **a long day out of coverage outruns the 10-hour window**: sign in at 07:00,
 * return to signal at 18:00, and no renewal is possible. That is expected, it is the most likely auth
 * failure this app will ever see, and it must cost nothing but one more sign-in.
 *
 * A technician signs in at the depot, drives to a site with no signal, and works for hours. Their
 * 15-minute access token expires in the car park. Everything after that is what this file is about:
 *
 *  · **An expired token clears NOTHING.** {@link OidcAuth.getAccessToken} returns `null` and the token
 *    set — including the refresh token — is retained. There is no path from an expiry to
 *    `outbox.clear()`, and there is no import of `outbox.ts` in this directory at all.
 *  · **`null` means "not sent", not "rejected".** `api.ts` returns `unauthenticated` **without calling
 *    `fetch`** when the provider yields no token, and `sync.ts` turns that into
 *    `releaseClaim(...)` — back to `queued`, **attempts decremented**, so a sign-in problem cannot
 *    exhaust a queue's attempt budget. That behaviour already exists; this file's job is not to break
 *    its premise.
 *  · **The outbox never asks for a token before accepting work.** `enqueue()` is a pure IndexedDB
 *    write. A signed-out, offline technician can fill in and submit forms all day; the queue drains
 *    later.
 *  · **Refresh happens BEFORE expiry, not after a 401.** A 401-driven refresh means the first request
 *    after every dead zone fails, and on a marginal connection the retry may not get a second chance.
 *    {@link REFRESH_SKEW_MS} pulls the renewal forward, and `online` / `visibilitychange` / a timer
 *    each trigger one — the same three triggers `sync.ts` uses, for the same reason (iOS Safari fires
 *    `online` late or never, and freezes timers in a backgrounded tab).
 *  · **A failed refresh is `session-stale`, not `session-expired`.** Only the IdP explicitly refusing the
 *    refresh token ends a session. Being offline, or the IdP not answering, leaves the session intact
 *    and recoverable with no typing. See `phase.ts` for why those need different words.
 *  · **This app never touches a credential.** Technicians self-register on the IdPrism portal with a
 *    password and a device-bound FIDO2 passkey. There is no password field, no passkey handling and no
 *    username input in this directory: {@link OidcAuth.beginSignIn} builds a URL and navigates.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHERE THE TOKENS LIVE: **one private field, and nowhere else.**
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * `#tokens` is a module-instance field. It is never serialised, never written to `localStorage`,
 * IndexedDB, a cookie or `sessionStorage`, and it dies with the tab. `transient.ts` explains the one
 * thing that IS written to storage (the single-use PKCE `code_verifier`) and why it is not a token.
 *
 * The cost is real and is stated rather than hidden: **relaunching the app from the home screen means
 * signing in again**, because there is no durable refresh token to resume from. `phase.ts`'s
 * `never-signed-in` copy says so. The alternative — a refresh token on disk on a shared field device —
 * is the threat Federate's own governance names, and it is not worth one fewer tap.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * ⚠️ WHAT HAS NOT BEEN EXERCISED
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * **NO LIVE SIGN-IN HAS BEEN PERFORMED, BY ANYONE.** No app client exists on the Federate service
 * profile yet, so the redirect has never been followed and no authorization code has ever been
 * exchanged. Every assertion in `test/auth/**` runs against an injected `fetch` or an injected
 * {@link TokenExchange}. The PKCE mathematics IS exercised against the real `crypto.subtle`; the
 * protocol conversation is not.
 *
 * Three specific things are therefore unverified, and each is named where it lives rather than here:
 *
 *  1. **Browser-side token exchange.** It is not established that Federate's token endpoint answers a
 *     cross-origin `fetch`. If it does not, the exchange needs a proxy — still a public client, still no
 *     secret. `exchange.ts` is the seam that makes that a small change, and explains why the proxy is
 *     deliberately not built on spec.
 *  2. **`S256` and secretless public-client support** are confirmed by Federate's own guide, IdPrism's
 *     integration guide, and `PRISMApp` shipping `usePKCE: true` with no secret. That is documentation
 *     and precedent, **not measurement** — client-id validation short-circuits before PKCE is reached,
 *     so it cannot be probed against the pool today.
 *  3. **The authorize and token endpoints themselves**, which were never observed and which differ
 *     between Federate prod and integ. `config.ts` demands them from configuration and fails by name.
 *
 * ⚠️ And one correction worth keeping: this file was first written against the **Cognito user pool's**
 * issuer and hosted UI. Those are the IdPrism↔Cognito internal leg — reachable, convincing, and not our
 * integration. We are on **Federate**. Nothing in this file changed to fix that, because no endpoint was
 * ever compiled into it; see `config.ts`.
 */

import type { TokenProvider } from '../core/auth';
import { CODE_CHALLENGE_METHOD, PLATFORM_REFRESH_TOKEN_TTL_MS, type OidcConfig } from './config';
import { directTokenExchange, type TokenEndpointBody, type TokenExchange } from './exchange';
import { decodeJwtClaims, expiryMsFromClaims, stringClaim } from './jwt';
import type { AuthPhase } from './phase';
import {
  createCodeVerifier,
  deriveCodeChallenge,
  randomUrlToken,
  webCryptoRandomBytes,
  webCryptoSha256,
  type RandomBytes,
  type Sha256,
} from './pkce';
import {
  isExpiredTransaction,
  memoryTransientStore,
  sessionTransientStore,
  type TransientStore,
} from './transient';

/**
 * Renew this long before the token actually expires.
 *
 * 90 seconds, chosen against the failure it prevents rather than as a round number: a submission that
 * starts with 20 seconds of validity left can easily finish after expiry on a slow connection, and the
 * 401 that follows costs a round trip and a retry the technician may not still be online for.
 *
 * **Comfortably under the platform's fixed 15-minute access token** — 90 s is 10% of it, so a token is
 * used for the first 13½ minutes of its life and renewed in the last 1½. Sized the other way (say 10
 * minutes of skew) the app would spend a third of every token's life refreshing, which on a marginal
 * connection is a self-inflicted request storm.
 */
export const REFRESH_SKEW_MS = 90_000;

/**
 * Used only when the token response omits `expires_in` **and** the access token has no readable `exp`.
 * Deliberately SHORT: assuming a long life for a token whose lifetime the server did not state is the
 * dangerous direction — it produces requests with a dead token and no attempt to renew. One minute
 * forces an early renewal instead, which is cheap and self-correcting.
 */
export const FALLBACK_TOKEN_LIFETIME_MS = 60_000;

/** How often to check whether a proactive refresh is due, while the app is in the foreground. */
const REFRESH_POLL_INTERVAL_MS = 60_000;

/** Query parameters the IdP adds to our redirect URI. Stripped so a reload cannot re-exchange. */
const CALLBACK_PARAMS = [
  'code',
  'state',
  'error',
  'error_description',
  'error_uri',
  'session_state',
  'iss',
] as const;

/** In memory, for the lifetime of the tab. Never serialised. */
interface TokenSet {
  readonly accessToken: string;
  readonly refreshToken: string | null;
  readonly idToken: string | null;
  /** Epoch ms. */
  readonly expiresAt: number;
  readonly obtainedAt: number;
  /**
   * Epoch ms the SESSION began — carried across refreshes, unlike `obtainedAt`. This is the clock the
   * platform's 10-hour refresh window runs on, and it is how a refusal after a long day offline is
   * distinguished from a refusal for any other reason.
   */
  readonly signedInAt: number;
  readonly scope: string | null;
  readonly subject: string | null;
}

export type RedirectResult =
  /** The URL carried no `code` and no `error`: an ordinary app load. */
  | { readonly kind: 'no-callback' }
  | { readonly kind: 'signed-in'; readonly expiresAt: number }
  /**
   * A callback arrived with nothing to match it to — a reload of the redirected URL, a bookmarked
   * callback, or a genuine replay. **No exchange is attempted.** The query string is cleaned anyway.
   */
  | { readonly kind: 'stale-callback'; readonly reason: 'no-transaction' | 'transaction-expired' }
  | { readonly kind: 'failed'; readonly code: string; readonly detail: string };

export interface OidcAuthOptions {
  readonly config: OidcConfig;
  /** Injected in tests. Defaults to the global. */
  readonly fetchImpl?: typeof fetch;
  /**
   * The token-exchange seam. Defaults to a direct browser `POST` to `config.tokenEndpoint`.
   *
   * Injectable because it may have to move off the browser entirely: it is not established whether
   * Federate's token endpoint answers a cross-origin `fetch`. See `exchange.ts` for the two proxy shapes
   * and why neither of them is built yet.
   */
  readonly exchange?: TokenExchange;
  readonly now?: () => number;
  readonly transient?: TransientStore;
  readonly randomBytes?: RandomBytes;
  readonly sha256?: Sha256;
  readonly onLine?: () => boolean;
  /** Defaults to a real navigation. Overridden in tests, which must not leave the page. */
  readonly navigate?: (url: string) => void;
  readonly currentUrl?: () => string | null;
  readonly replaceUrl?: (url: string) => void;
  readonly refreshSkewMs?: number;
}

export class OidcAuth implements TokenProvider {
  readonly kind = 'oidc-pkce' as const;

  readonly config: OidcConfig;

  /** ⚠️ THE ONLY PLACE TOKENS EXIST. See the file header. */
  #tokens: TokenSet | null = null;

  #phase: AuthPhase = { kind: 'never-signed-in' };

  #refreshInFlight: Promise<string | null> | null = null;

  /**
   * Whether this session has already failed to renew at least once (offline, or the IdP unreachable).
   *
   * It is the evidence that turns a later refusal into `offline-too-long` rather than a bare `refused`:
   * a refresh token that stopped being renewable while there was no signal, and is then rejected the
   * moment signal returns, is the 10-hour window having elapsed. Without this the app would report an
   * expected end-of-shift expiry as if something had gone wrong at Amazon's end.
   */
  #wentStale = false;

  #listeners = new Set<(phase: AuthPhase) => void>();

  #stopFns: (() => void)[] = [];

  readonly #exchange: TokenExchange;
  readonly #now: () => number;
  readonly #transient: TransientStore;
  readonly #randomBytes: RandomBytes;
  readonly #sha256: Sha256;
  readonly #onLine: () => boolean;
  readonly #navigate: (url: string) => void;
  readonly #currentUrl: () => string | null;
  readonly #replaceUrl: (url: string) => void;
  readonly #skewMs: number;

  constructor(options: OidcAuthOptions) {
    this.config = options.config;
    const fetchImpl = options.fetchImpl ?? ((...args: Parameters<typeof fetch>) => globalThis.fetch(...args));
    this.#exchange = options.exchange ?? directTokenExchange(options.config.tokenEndpoint, fetchImpl);
    this.#now = options.now ?? (() => Date.now());
    this.#transient =
      options.transient ?? (globalThis.sessionStorage === undefined ? memoryTransientStore() : sessionTransientStore());
    this.#randomBytes = options.randomBytes ?? webCryptoRandomBytes;
    this.#sha256 = options.sha256 ?? webCryptoSha256;
    // `undefined` navigator (a non-browser runtime) counts as ONLINE, matching `api.ts`: treating
    // unknown as offline would make the refresher refuse to run anywhere without a `navigator`.
    this.#onLine = options.onLine ?? (() => globalThis.navigator === undefined || globalThis.navigator.onLine !== false);
    this.#navigate =
      options.navigate ??
      ((url) => {
        // `assign`, not `replace`: the technician's back gesture should return them to the app, not
        // skip past it into whatever they were looking at before.
        globalThis.location?.assign(url);
      });
    this.#currentUrl = options.currentUrl ?? (() => globalThis.location?.href ?? null);
    this.#replaceUrl =
      options.replaceUrl ??
      ((url) => {
        globalThis.history?.replaceState(null, '', url);
      });
    this.#skewMs = options.refreshSkewMs ?? REFRESH_SKEW_MS;
  }

  // -------------------------------------------------------------------------
  // The TokenProvider contract
  // -------------------------------------------------------------------------

  /**
   * A usable bearer token, or `null`.
   *
   * `null` is a normal, non-destructive answer. It means "nothing can be sent right now", and `api.ts`
   * turns it into `unauthenticated` **without sending anything**. It does not mean the session is over
   * and it never discards anything.
   *
   * SINGLE-FLIGHT, as `core/auth.ts` requirement 3 demands: twelve queued submissions draining at once
   * share one refresh. Without that, twelve concurrent refresh POSTs race, and on a pool with refresh
   * rotation enabled eleven of them would be using an already-rotated token.
   */
  async getAccessToken(): Promise<string | null> {
    const held = this.#tokens;
    if (held === null) {
      return null;
    }
    if (this.#now() < held.expiresAt - this.#skewMs) {
      return held.accessToken;
    }

    const renewed = await this.#refreshSingleFlight();
    if (renewed !== null) {
      return renewed;
    }

    /*
     * The renewal failed, but the token we already have may STILL BE VALID — we renew `#skewMs`
     * early, so this branch is reachable with real validity left. Throwing it away because a
     * proactive refresh failed would turn a working request into a 401 for no reason.
     */
    const current = this.#tokens;
    if (current !== null && this.#now() < current.expiresAt) {
      return current.accessToken;
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // State, for the UI
  // -------------------------------------------------------------------------

  phase(): AuthPhase {
    return this.#phase;
  }

  subscribe(listener: (phase: AuthPhase) => void): () => void {
    this.#listeners.add(listener);
    listener(this.#phase);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  #setPhase(phase: AuthPhase): void {
    this.#phase = phase;
    for (const listener of this.#listeners) {
      try {
        listener(phase);
      } catch {
        // A throwing subscriber (a React render error) must not abort a token transition that has
        // already happened. Same reasoning as `outbox.ts`'s notify loop.
      }
    }
  }

  // -------------------------------------------------------------------------
  // Starting a sign-in
  // -------------------------------------------------------------------------

  /**
   * Build the authorization URL, remember the PKCE transaction, and navigate.
   *
   * `state` and `nonce` are freshly generated **per attempt** — never derived from anything, never
   * reused across attempts, and never predictable. Reusing a `state` would make the mismatch check
   * below meaningless, which is the quiet way to have a CSRF defence that defends nothing.
   */
  async beginSignIn(options: { readonly navigate?: boolean } = {}): Promise<
    { readonly ok: true; readonly url: string } | { readonly ok: false; readonly code: string; readonly detail: string }
  > {
    try {
      const codeVerifier = createCodeVerifier(undefined, this.#randomBytes);
      const codeChallenge = await deriveCodeChallenge(codeVerifier, this.#sha256);
      const state = randomUrlToken(undefined, this.#randomBytes);
      const nonce = randomUrlToken(undefined, this.#randomBytes);

      this.#transient.put({
        state,
        nonce,
        codeVerifier,
        startedAt: this.#now(),
        redirectUri: this.config.redirectUri,
      });

      const url = new URL(this.config.authorizationEndpoint);
      url.searchParams.set('response_type', 'code');
      url.searchParams.set('client_id', this.config.clientId);
      url.searchParams.set('redirect_uri', this.config.redirectUri);
      url.searchParams.set('scope', this.config.scopes.join(' '));
      url.searchParams.set('state', state);
      url.searchParams.set('nonce', nonce);
      url.searchParams.set('code_challenge', codeChallenge);
      url.searchParams.set('code_challenge_method', CODE_CHALLENGE_METHOD);

      this.#setPhase({ kind: 'signing-in' });
      const href = url.toString();
      if (options.navigate !== false) {
        this.#navigate(href);
      }
      return { ok: true, url: href };
    } catch (error) {
      const detail = error instanceof Error ? error.message : String(error);
      this.#setPhase({ kind: 'sign-in-failed', code: 'could-not-start', detail });
      return { ok: false, code: 'could-not-start', detail };
    }
  }

  // -------------------------------------------------------------------------
  // Returning from the IdP
  // -------------------------------------------------------------------------

  /**
   * Handle the redirect back from the IdP. Safe to call on every app load.
   *
   * THE ORDER OF THE CHECKS IS THE CONTENT OF THIS METHOD:
   *
   *  1. no `code` and no `error` → an ordinary load. Nothing is touched, not even the URL.
   *  2. **take the transaction (read AND delete)** — so a second call, or a reload, finds nothing.
   *  3. no transaction, or an expired one → `stale-callback`. **No exchange.** A used authorization code
   *     replayed against the token endpoint is at best an `invalid_grant`; at worst, in a flow with a
   *     retained verifier, it is a replay we performed ourselves.
   *  4. **`state` mismatch → ABORT.** Not a warning, not a log line, not "continue anyway". A response
   *     whose `state` is not the one we generated is a response to somebody else's request, and
   *     exchanging its code would sign this app in as whoever forged it.
   *  5. `error` from the IdP → `sign-in-failed`, carrying the IdP's own code.
   *  6. exchange, and verify the `nonce` in the returned id_token.
   *
   * The query string is cleaned in every branch that saw a callback, **including the failures** — a
   * `code` left in the address bar is re-submitted by the next reload, by the browser restoring the
   * tab, and by anything that shares the URL.
   */
  async handleRedirect(rawUrl?: string): Promise<RedirectResult> {
    const href = rawUrl ?? this.#currentUrl();
    if (href === null) {
      return { kind: 'no-callback' };
    }

    let url: URL;
    try {
      url = new URL(href);
    } catch {
      return { kind: 'no-callback' };
    }

    const code = url.searchParams.get('code');
    const error = url.searchParams.get('error');
    if (code === null && error === null) {
      return { kind: 'no-callback' };
    }

    /*
     * EVERY PARAMETER IS READ BEFORE THE URL IS CLEANED. `#cleanCallbackParams` deletes them from this
     * same `URL` object, so reading `error_description` afterwards returned `null` and the IdP's own
     * explanation was replaced by a generic sentence — found by the test that asserts the description
     * reaches the phase, not by reading this code.
     */
    const returnedState = url.searchParams.get('state');
    const errorDescription = url.searchParams.get('error_description');

    const transaction = this.#transient.take();
    this.#cleanCallbackParams(url);

    if (transaction === null) {
      /*
       * Not reported as a failure, and deliberately not shown as one: the overwhelmingly common cause
       * is a reload of the post-sign-in URL, where the app is already signed in and nothing is wrong.
       * The phase is left alone rather than being knocked to `sign-in-failed`.
       */
      return { kind: 'stale-callback', reason: 'no-transaction' };
    }
    if (isExpiredTransaction(transaction, this.#now())) {
      return { kind: 'stale-callback', reason: 'transaction-expired' };
    }

    if (returnedState === null || returnedState !== transaction.state) {
      return this.#fail(
        'state-mismatch',
        returnedState === null
          ? 'the sign-in response carried no state value'
          : 'the sign-in response carried a state value this app did not generate',
      );
    }

    if (error !== null) {
      return this.#fail(error, errorDescription ?? 'the sign-in service refused the request');
    }
    if (code === null) {
      return this.#fail('no-code', 'the sign-in response carried neither a code nor an error');
    }

    this.#setPhase({ kind: 'signing-in' });

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: transaction.redirectUri,
      client_id: this.config.clientId,
      code_verifier: transaction.codeVerifier,
      // NO `client_secret`. See `config.ts` — this is a public client and a secret in a browser
      // bundle is a published credential. Federate's own guidance for a PKCE public client is
      // "credentials MUST NOT be sent; authenticate by including your client_id".
    });

    const exchanged = await this.#exchange.post(body);
    if (!exchanged.ok) {
      return this.#fail(exchanged.code, exchanged.detail);
    }

    const applied = this.#applyTokenResponse(exchanged.body, {
      expectedNonce: transaction.nonce,
      previousRefreshToken: null,
      // A fresh sign-in: the 10-hour refresh window starts now.
      signedInAt: null,
    });
    if (!applied.ok) {
      return this.#fail(applied.code, applied.detail);
    }

    this.#wentStale = false;
    this.#setPhase({
      kind: 'signed-in',
      expiresAt: applied.expiresAt,
      signedInAt: applied.signedInAt,
      subject: applied.subject,
    });
    return { kind: 'signed-in', expiresAt: applied.expiresAt };
  }

  #fail(code: string, detail: string): RedirectResult {
    // The transaction is already gone (taken above). Clearing again is belt and braces for the paths
    // that fail before the take.
    this.#transient.clear();
    this.#setPhase({ kind: 'sign-in-failed', code, detail });
    return { kind: 'failed', code, detail };
  }

  /**
   * Strip the callback parameters, **preserving the hash**.
   *
   * The hash is not optional: `AppShell` routes on it (`#/outbox`), so replacing the URL without it
   * would silently navigate the technician back to the Jobs view mid-sign-in.
   */
  #cleanCallbackParams(url: URL): void {
    let changed = false;
    for (const key of CALLBACK_PARAMS) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (!changed) {
      return;
    }
    const search = url.searchParams.toString();
    const next = `${url.pathname}${search.length > 0 ? `?${search}` : ''}${url.hash}`;
    try {
      this.#replaceUrl(next);
    } catch {
      // `replaceState` throws on a cross-origin or opaque-origin document. A dirty address bar is
      // worse than this catch, but it is not worth failing a completed sign-in over.
    }
  }

  // -------------------------------------------------------------------------
  // Refresh
  // -------------------------------------------------------------------------

  /**
   * Renew now if a renewal is due. Called by the `online` / `visibilitychange` / poll triggers, and
   * callable directly. A no-op when there is nothing to renew.
   */
  async refreshIfDue(): Promise<void> {
    const held = this.#tokens;
    if (held === null) {
      return;
    }
    if (this.#now() < held.expiresAt - this.#skewMs) {
      return;
    }
    await this.#refreshSingleFlight();
  }

  #refreshSingleFlight(): Promise<string | null> {
    const existing = this.#refreshInFlight;
    if (existing !== null) {
      return existing;
    }
    const started = this.#performRefresh().finally(() => {
      this.#refreshInFlight = null;
    });
    this.#refreshInFlight = started;
    return started;
  }

  /**
   * One refresh attempt, and the classification of its outcome — which is the important part.
   *
   *   offline                       → `session-stale('offline')`.         TOKENS KEPT.
   *   `fetch` rejected / 5xx / 429  → `session-stale('idp-unreachable')`. TOKENS KEPT.
   *   4xx from the IdP              → `session-expired`.                 Tokens cleared, QUEUE UNTOUCHED.
   *   200                           → `signed-in`.
   *
   * **The 4xx/5xx split is the whole judgement call.** A 400 `invalid_grant` means the refresh token is
   * genuinely dead and no amount of waiting revives it, so continuing to present the technician as
   * signed in would be a lie that costs them a shift. A 502 from an overloaded edge says nothing at all
   * about the token, and treating it as a sign-out would log people out during an outage — while their
   * queue sat there needing exactly the session we had just discarded.
   *
   * **Neither branch touches the outbox.** A cleared token set is still only a token set: there is no
   * import of `outbox.ts` in this file, so "expired" cannot mean "lost" even by accident.
   */
  async #performRefresh(): Promise<string | null> {
    const held = this.#tokens;
    if (held === null) {
      return null;
    }
    if (held.refreshToken === null) {
      /*
       * Nothing to renew with. A real end of session — but note what it is NOT: not a failure, and not
       * the technician's problem. An app client configured without `refresh_token` in its grant list
       * produces exactly this, and it should read as a setup finding rather than as an error nobody
       * can act on.
       */
      this.#tokens = null;
      this.#setPhase({
        kind: 'session-expired',
        reason: 'no-refresh-token',
        detail: 'the access token expired and the sign-in service issued no refresh token',
        signedInAt: held.signedInAt,
      });
      return null;
    }

    if (!this.#onLine()) {
      /*
       * THE DEAD-ZONE PATH, AND IT IS DELIBERATELY INERT. No request, no token cleared, no state
       * change beyond a label the UI can read. `#wentStale` is recorded so that if the eventual
       * reconnect meets a refusal, it can be reported as the 10-hour window elapsing rather than as
       * something having gone wrong.
       */
      this.#wentStale = true;
      this.#setPhase({ kind: 'session-stale', expiredAt: held.expiresAt, cause: 'offline' });
      return null;
    }

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: held.refreshToken,
      client_id: this.config.clientId,
      // No `client_secret`: Federate verifies a secret on refresh *unless* the profile is
      // `pkceRequired`. See `discovery/auth/idprism.md` §6, confirmation 3.
    });

    const response = await this.#exchange.post(body);
    if (!response.ok) {
      if (response.retryable) {
        this.#wentStale = true;
        this.#setPhase({ kind: 'session-stale', expiredAt: held.expiresAt, cause: 'idp-unreachable' });
        return null;
      }
      /*
       * The IdP refused the refresh token. The session is over — but WHY it is over changes what the
       * technician is told, and the two cases are not equally likely.
       *
       * `offline-too-long` is the expected one: a shift longer than the 10-hour refresh window. It is
       * inferred from two independent signals rather than from the error code, because the IdP answers
       * a plain `invalid_grant` either way and cannot tell us which:
       *   · the session began more than the platform's refresh window ago — arithmetic, not a guess; or
       *   · a renewal had already failed for want of signal (`#wentStale`), meaning the token was
       *     unrenewable during precisely the period that would have consumed the window.
       * Anything else is `refused`, which is genuinely unusual (a disabled account, a changed app
       * client) and is worth a technician reporting.
       */
      const sessionAgeMs = this.#now() - held.signedInAt;
      const outranWindow = sessionAgeMs >= PLATFORM_REFRESH_TOKEN_TTL_MS;
      this.#tokens = null;
      this.#setPhase({
        kind: 'session-expired',
        reason: outranWindow || this.#wentStale ? 'offline-too-long' : 'refused',
        detail: `${response.code}: ${response.detail}`,
        signedInAt: held.signedInAt,
      });
      return null;
    }

    const applied = this.#applyTokenResponse(response.body, {
      // No nonce check on refresh: there is no authorization request to bind to, and OIDC does not
      // require a `nonce` in a refreshed id_token.
      expectedNonce: null,
      // Cognito returns the SAME refresh token on refresh and may omit it from the body entirely.
      // Dropping it because the response did not repeat it would end the session on the next expiry.
      previousRefreshToken: held.refreshToken,
      signedInAt: held.signedInAt,
    });
    if (!applied.ok) {
      // A 200 whose body we cannot use. The tokens we hold are untouched, so this is stale rather
      // than ended — a malformed response is not evidence that the refresh token is dead.
      this.#wentStale = true;
      this.#setPhase({ kind: 'session-stale', expiredAt: held.expiresAt, cause: 'idp-unreachable' });
      return null;
    }

    this.#wentStale = false;
    this.#setPhase({
      kind: 'signed-in',
      expiresAt: applied.expiresAt,
      signedInAt: applied.signedInAt,
      subject: applied.subject,
    });
    return applied.accessToken;
  }

  // -------------------------------------------------------------------------
  // The token endpoint
  // -------------------------------------------------------------------------

  /**
   * Validate a token response and install it. Returns the computed expiry rather than setting the
   * phase, so the two callers can classify their own failures differently.
   */
  #applyTokenResponse(
    body: TokenEndpointBody,
    context: {
      readonly expectedNonce: string | null;
      readonly previousRefreshToken: string | null;
      /** Carried across a refresh; `null` on a fresh sign-in, where the session starts now. */
      readonly signedInAt: number | null;
    },
  ):
    | {
        readonly ok: true;
        readonly accessToken: string;
        readonly expiresAt: number;
        readonly signedInAt: number;
        readonly subject: string | null;
      }
    | { readonly ok: false; readonly code: string; readonly detail: string } {
    const accessToken = typeof body.access_token === 'string' ? body.access_token : '';
    if (accessToken.length === 0) {
      return { ok: false, code: 'no-access-token', detail: 'the token response carried no access_token' };
    }

    const idToken = typeof body.id_token === 'string' && body.id_token.length > 0 ? body.id_token : null;
    const idClaims = decodeJwtClaims(idToken);

    if (context.expectedNonce !== null) {
      /*
       * NONCE VERIFICATION, AND IT IS STRICT ON PURPOSE.
       *
       * We asked for `openid`, so an id_token must come back; a response without one, or with a
       * `nonce` that is not the one we generated, is not an answer to our request. Accepting it
       * "because the access token looks fine" is how a nonce check becomes decoration.
       *
       * ⚠️ Honest limitation: the id_token's SIGNATURE IS NOT VERIFIED here (see `jwt.ts`). This
       * check binds the response to our request; it does not authenticate the issuer. The API
       * authorizer does that, against `jwks_uri`, server-side.
       */
      if (idToken === null) {
        return { ok: false, code: 'no-id-token', detail: 'the token response carried no id_token to check the nonce against' };
      }
      const returnedNonce = stringClaim(idClaims, 'nonce');
      if (returnedNonce === null || returnedNonce !== context.expectedNonce) {
        return {
          ok: false,
          code: 'nonce-mismatch',
          detail: 'the id_token did not carry the nonce this app generated for this sign-in',
        };
      }
    }

    const expiresAt = computeExpiry(body.expires_in, accessToken, this.#now());
    const refreshToken =
      typeof body.refresh_token === 'string' && body.refresh_token.length > 0
        ? body.refresh_token
        : context.previousRefreshToken;

    const subject = stringClaim(idClaims, 'sub') ?? stringClaim(decodeJwtClaims(accessToken), 'sub');
    const obtainedAt = this.#now();
    // A refresh does NOT restart the 10-hour window. Re-basing it here would make the app believe an
    // expired session was fresh, and the wording for the 07:00-to-18:00 case would never be reached.
    const signedInAt = context.signedInAt ?? obtainedAt;

    this.#tokens = {
      accessToken,
      refreshToken,
      idToken,
      expiresAt,
      obtainedAt,
      signedInAt,
      scope: typeof body.scope === 'string' ? body.scope : null,
      subject,
    };
    return { ok: true, accessToken, expiresAt, signedInAt, subject };
  }

  // -------------------------------------------------------------------------
  // Sign-out, and the background triggers
  // -------------------------------------------------------------------------

  /**
   * Drop every token this tab holds and return the URL that ends the IdP's own session.
   *
   * **Local state is cleared unconditionally, before anything network-shaped happens**, so signing out
   * works with no signal. The end-session redirect is returned rather than performed, because
   * navigating away is the caller's decision.
   *
   * ⚠️ TWO HONEST LIMITATIONS.
   *  · `revocation_endpoint` is carried in configuration and is **never called**. A revoke is a network
   *    request that can fail, and a sign-out that fails because the phone is in a basement is worse
   *    than one that leaves a refresh token to expire on its own inside its 10-hour window. Since no
   *    refresh token is ever written to disk, clearing memory really does destroy this device's copy.
   *  · `discovery/auth/idprism.md` §3 warns that IdPrism has FOUR session layers and that missing the
   *    IdPrism one produces the classic *"logged out, logged back in, never asked for credentials"*
   *    bug — which matters on a tablet handed between technicians. The end-session URL below addresses
   *    the Cognito hosted-UI layer; whether that is sufficient for the IdPrism layer behind it is
   *    **unverified**, and the discovery document and the endpoint list handed to this workstream
   *    disagree about whether an `end_session_endpoint` exists at all.
   */
  signOut(): { readonly endSessionUrl: string | null } {
    this.#tokens = null;
    this.#transient.clear();
    this.#refreshInFlight = null;
    this.#wentStale = false;
    this.#setPhase({ kind: 'never-signed-in' });

    // `null` rather than a constructed guess when the endpoint is not configured. Local state is
    // already cleared at this point, so a missing end-session URL costs the IdP's own session, not
    // this device's — and inventing a URL would be a fabricated endpoint in security-relevant code.
    if (this.config.endSessionEndpoint === null) {
      return { endSessionUrl: null };
    }
    const url = new URL(this.config.endSessionEndpoint);
    url.searchParams.set('client_id', this.config.clientId);
    url.searchParams.set('logout_uri', this.config.redirectUri);
    return { endSessionUrl: url.toString() };
  }

  /**
   * Attach the proactive-refresh triggers. Returns a stop function.
   *
   * THREE TRIGGERS, matching `sync.ts` because the same environment defeats the same assumptions:
   * `online` (fires late or never on iOS Safari behind a captive portal), `visibilitychange` (the
   * moment a technician brings the app back is the most likely moment for connectivity to have
   * changed, and iOS freezes timers in a background tab), and a poll (covers a renewal coming due
   * while the app simply sits open).
   */
  start(): () => void {
    const tick = (): void => {
      void this.refreshIfDue().catch(() => undefined);
    };
    const onVisible = (): void => {
      if (globalThis.document?.visibilityState === 'visible') {
        tick();
      }
    };

    globalThis.addEventListener?.('online', tick);
    globalThis.document?.addEventListener('visibilitychange', onVisible);
    const timer = globalThis.setInterval(tick, REFRESH_POLL_INTERVAL_MS);

    const stop = (): void => {
      globalThis.removeEventListener?.('online', tick);
      globalThis.document?.removeEventListener('visibilitychange', onVisible);
      globalThis.clearInterval(timer);
    };
    this.#stopFns.push(stop);
    return stop;
  }

  /** Tear down every `start()` this instance handed out. For tests and for a clean teardown. */
  stop(): void {
    for (const stop of this.#stopFns) {
      stop();
    }
    this.#stopFns = [];
  }

  /* ------------------------------------------------------------ diagnostics ---- */

  /**
   * Epoch ms of the held token's expiry, or `null`. **Never returns the token itself** — there is no
   * accessor on this class that hands a caller the raw token except `getAccessToken`, which exists to
   * put it in one `Authorization` header.
   */
  expiresAt(): number | null {
    return this.#tokens?.expiresAt ?? null;
  }

  /** True when a refresh token is held, i.e. the session can recover without the technician acting. */
  canRenewWithoutUser(): boolean {
    const held = this.#tokens;
    return held !== null && held.refreshToken !== null;
  }

  /** Epoch ms the session began, or `null`. The clock the 10-hour refresh window runs on. */
  signedInAt(): number | null {
    return this.#tokens?.signedInAt ?? null;
  }
}

/**
 * `expires_in` (seconds) → an absolute epoch-ms deadline.
 *
 * An ABSOLUTE deadline, not a duration, because a duration has to be re-based against a clock read
 * somewhere and the somewhere is always wrong: a phone that sleeps for two hours wakes with a
 * "remaining" value that never ticked down.
 */
function computeExpiry(expiresIn: unknown, accessToken: string, now: number): number {
  if (typeof expiresIn === 'number' && Number.isFinite(expiresIn) && expiresIn > 0) {
    return now + expiresIn * 1000;
  }
  // Some deployments omit `expires_in`. The access token's own `exp` is the better second source; it
  // is the server's statement rather than our guess.
  const fromClaims = expiryMsFromClaims(decodeJwtClaims(accessToken));
  if (fromClaims !== null) {
    return fromClaims;
  }
  return now + FALLBACK_TOKEN_LIFETIME_MS;
}

